import {
  ALWAYS_VISIBLE_SIZE_KEY,
  DEFAULT_LOCAL_SIZE_KEY,
  SIZE_DIMENSION_KEYS,
  type LocalizedString,
} from '@onrunning/backstage-models';
import { BackstageTheme } from '../models/backstage-theme.enum';

const fallBackLocale: keyof LocalizedString = 'en-US';
const sizeTypes: string[] = [...SIZE_DIMENSION_KEYS];

export const globalConstants = {
  sizeTypes,
  defaultSizeType: DEFAULT_LOCAL_SIZE_KEY,
  alwaysVisibleSizeType: ALWAYS_VISIBLE_SIZE_KEY,
  defaultLocale: navigator.languages?.[0] ?? navigator.language ?? fallBackLocale,
  fallBackLocale: fallBackLocale as keyof LocalizedString,
  theme: BackstageTheme.light,
  dateFormat: 'YYYY-MM-DD',
  dateLocalFormat: 'L',
  earliestPickupDayOffset: 1, // Number of days from the current day
  countryCodeAndCountryDelimiter: '---',
  maximumDate: '9999-12-31',
  defaultDateFormatOption: {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  } as const satisfies Intl.DateTimeFormatOptions,
};

export interface State {
  code: string;
  name: string;
}

export interface Country {
  code: string;
  name: string;
}

export enum CountryList {
  ca = 'canada',
  us = 'united states',
  au = 'australia',
  de = 'germany',
  jp = 'japan',
  br = 'brazil',
  uk = 'united kingdom',
  gb = 'united kingdom',
  esp = 'spain',
}

export const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const countryNameToISOCountryMap: Record<string, string> = {
  Benelux: 'nl',
  Switzerland: 'ch',
  France: 'fr',
  'United Kingdom': 'gb',
  Germany: 'de',
  Austria: 'at',
  Brazil: 'br',
  Australia: 'au',
  Canada: 'ca',
  'United States': 'us',
  Japan: 'jp',
  China: 'cn',
  Spain: 'es',
};

export const countriesWithStates: Record<string, State[]> = Object.freeze({
  [CountryList.ca]: [
    { code: 'AB', name: 'Alberta' },
    { code: 'BC', name: 'British Columbia' },
    { code: 'MB', name: 'Manitoba' },
    { code: 'NB', name: 'New Brunswick' },
    { code: 'NL', name: 'Newfoundland and Labrador' },
    { code: 'NS', name: 'Nova Scotia' },
    { code: 'NT', name: 'Northwest Territories' },
    { code: 'NU', name: 'Nunavut' },
    { code: 'ON', name: 'Ontario' },
    { code: 'PE', name: 'Prince Edward Island' },
    { code: 'QC', name: 'Quebec' },
    { code: 'SK', name: 'Saskatchewan' },
    { code: 'YT', name: 'Yukon Territories' },
  ],
  [CountryList.us]: [
    { code: 'AA', name: 'Armed Forces Americas' },
    { code: 'AE', name: 'Armed Forces Europe' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AL', name: 'Alabama' },
    { code: 'AP', name: 'Armed Forces Pacific' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'AS', name: 'American Samoa' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DC', name: 'District of Columbia' },
    { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' },
    { code: 'FM', name: 'Micronesia' },
    { code: 'GA', name: 'Georgia' },
    { code: 'GU', name: 'Guam' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'IA', name: 'Iowa' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MD', name: 'Maryland' },
    { code: 'ME', name: 'Maine' },
    { code: 'MH', name: 'Marshall Islands' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MP', name: 'Northern Mariana Islands' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MT', name: 'Montana' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NY', name: 'New York' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'PR', name: 'Puerto Rico' },
    { code: 'PW', name: 'Palau' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UM', name: 'U.S. Minor Outlying Islands' },
    { code: 'UT', name: 'Utah' },
    { code: 'VA', name: 'Virginia' },
    { code: 'VI', name: 'U.S. Virgin Islands' },
    { code: 'VT', name: 'Vermont' },
    { code: 'WA', name: 'Washington' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WY', name: 'Wyoming' },
  ],
  [CountryList.au]: [
    { code: 'SA', name: 'South Australia' },
    { code: 'VIC', name: 'Victoria' },
    { code: 'NSW', name: 'New South Wales' },
    { code: 'NT', name: 'Northern Territory' },
    { code: 'ACT', name: 'Australian Capital Territory' },
    { code: 'QLD', name: 'Queensland' },
    { code: 'WA', name: 'Western Australia' },
    { code: 'TAS', name: 'Tasmania' },
  ],
  [CountryList.esp]: [
    { code: 'A', name: 'Alicante' },
    { code: 'AB', name: 'Albacete' },
    { code: 'AL', name: 'Almer a' },
    { code: 'AN', name: 'Andalusia' },
    { code: 'AR', name: 'Aragon' },
    { code: 'AS', name: 'Asturias' },
    { code: 'AV', name: 'vila' },
    { code: 'B', name: 'Barcelona' },
    { code: 'BA', name: 'Badajoz' },
    { code: 'BI', name: 'Biscay' },
    { code: 'BU', name: 'Burgos' },
    { code: 'C', name: 'A Coru ±a' },
    { code: 'CA', name: 'C diz' },
    { code: 'CAT', name: 'Cataluña' },
    { code: 'CB', name: 'Cantabria' },
    { code: 'CC', name: 'C ceres' },
    { code: 'CE', name: 'Ceuta' },
    { code: 'CL', name: 'Castile and Le n' },
    { code: 'CM', name: 'Castile-La Mancha' },
    { code: 'CN', name: 'Canary Islands' },
    { code: 'CO', name: 'C rdoba' },
    { code: 'CR', name: 'Ciudad Real' },
    { code: 'CS', name: 'Castell n' },
    { code: 'CT', name: 'Catalonia' },
    { code: 'CU', name: 'Cuenca' },
    { code: 'EX', name: 'Extremadura' },
    { code: 'GA', name: 'Galicia' },
    { code: 'GC', name: 'Las Palmas' },
    { code: 'GI', name: 'Girona' },
    { code: 'GR', name: 'Granada' },
    { code: 'GU', name: 'Guadalajara' },
    { code: 'H', name: 'Huelva' },
    { code: 'HU', name: 'Huesca' },
    { code: 'IB', name: 'Balearic Islands' },
    { code: 'J', name: 'Ja n' },
    { code: 'L', name: 'Lleida' },
    { code: 'LE', name: 'Le n' },
    { code: 'LO', name: 'La Rioja Province' },
    { code: 'LU', name: 'Lugo' },
    { code: 'M', name: 'Madrid Province' },
    { code: 'MA', name: 'M laga' },
    { code: 'MC', name: 'Murcia Region' },
    { code: 'MD', name: 'Madrid Autonomous Community' },
    { code: 'ML', name: 'Melilla' },
    { code: 'MU', name: 'Murcia' },
    { code: 'NA', name: 'Navarra' },
    { code: 'NC', name: 'Navarra Chartered Community' },
    { code: 'O', name: 'Asturias Province' },
    { code: 'OR', name: 'Ourense' },
    { code: 'P', name: 'Palencia' },
    { code: 'PM', name: 'Balears Province' },
    { code: 'PO', name: 'Pontevedra' },
    { code: 'PV', name: 'Basque Country' },
    { code: 'RI', name: 'La Rioja' },
    { code: 'S', name: 'Cantabria Province' },
    { code: 'SA', name: 'Salamanca' },
    { code: 'SE', name: 'Seville' },
    { code: 'SG', name: 'Segovia' },
    { code: 'SO', name: 'Soria' },
    { code: 'SS', name: 'Gipuzkoa' },
    { code: 'T', name: 'Tarragona' },
    { code: 'TE', name: 'Teruel' },
    { code: 'TF', name: 'Santa Cruz de Tenerife' },
    { code: 'TO', name: 'Toledo' },
    { code: 'V', name: 'Valencia' },
    { code: 'VA', name: 'Valladolid' },
    { code: 'VC', name: 'Valencian Community' },
    { code: 'VI', name: 'lava' },
    { code: 'Z', name: 'Zaragoza' },
    { code: 'ZA', name: 'Zamora' },
  ],
});
